import { IconContactCS, IconContactCSLarge } from "@assets/icons";
import { openContactLinksDialog } from "@components/layouts/ContactLinksDialog/utils";
import useContactLinks from "@hooks/useContactLinks";
import { Button, PropsWithAria, cn } from "kz-ui-sdk";

interface ContactButtonGroupProps {
  className?: string;
  size?: "sm" | "md";
}

const ContactButtonGroup = ({ className, size = "sm" }: ContactButtonGroupProps) => {
  const { contactLinks, groupedContactIcon } = useContactLinks();

  if (!contactLinks?.length) {
    return null;
  }

  // If there's only one contact link, render a single button
  if (contactLinks.length === 1) {
    const contact = contactLinks[0];
    return (
      <a
        href={contact.url}
        target={"_blank"}
        rel={"noreferrer noopener"}
      >
        <ContactButton
          image={contact.image}
          description={contact.description}
          className={className}
          size={size}
          aria-label={`Header contact ${contact.description}`}
        />
      </a>
    );
  }

  // If there are multiple contact links
  return (
    <>
      <ContactButton
        image={groupedContactIcon}
        onClick={() => openContactLinksDialog()}
        aria-label="Header contact group"
        className={className}
        size={size}
      />
    </>
  );
};

export default ContactButtonGroup;

interface ContactButtonProps extends PropsWithAria {
  image?: string;
  description?: string;
  onClick?: () => void;
  className?: string;
  size?: "sm" | "md";
}

export const ContactButton = ({
  image,
  description,
  onClick,
  className,
  size = "sm",
  ["aria-label"]: ariaLabel,
}: ContactButtonProps) => {
  if (!image) {
    return (
      <Button
        className={cn(
          "flex shrink-0 items-center justify-center !rounded !border-none !bg-darkBrand-700 px-1.5",
          size === "sm" ? "!h-[30px] !w-[36px]" : "!h-[40px] !w-[48px]",
          className,
        )}
        onClick={onClick}
        variant="bordered"
        aria-label={ariaLabel}
        data-element-id="button-contact"
      >
        {size === "sm" ? (
          <IconContactCS
            className="text-content-base"
            aria-label={ariaLabel}
          />
        ) : (
          <IconContactCSLarge
            className="text-content-base"
            aria-label={ariaLabel}
          />
        )}
      </Button>
    );
  }

  return (
    <Button
      className={cn(
        "flex shrink-0 items-center justify-center overflow-hidden !rounded !border-none",
        size === "sm" ? "!h-[30px] !w-[36px]" : "!h-[40px] !w-[48px]",
        className,
      )}
      onClick={onClick}
      variant="bordered"
      data-element-id="button-contact"
    >
      <img
        src={image}
        alt={description}
        className="h-full w-full !rounded object-cover object-center"
        aria-label={ariaLabel}
      />
    </Button>
  );
};
